/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IPriceComponentResources } from './components/price.component';
import { ProductPrice, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import MsDyn365 from '@msdyn365-commerce/core';
import { Button, getTelemetryObject, IPopupProps, Module, Node, Popup } from '@msdyn365-commerce-modules/utilities';
import React, { PropsWithChildren, useState } from 'react';
import { ISearchResultContainerProps } from '../definition-extensions/search-result-container.ext.props.autogenerated';
import { ProductComponent } from './components/product.component';
import { getUrlSync, IGridSettings, IImageData, IImageProps, IImageSettings, Image, IRequestContext } from '@msdyn365-commerce/core';
import { AddToCartListComponent } from './components/addToCart.component';
import { IProductSearchResultExtensionData } from './product-result-container.data';
import Table from 'reactstrap/lib/Table';
import { LoginToViewPricingComponent } from '../../../components/LoginToViewPricing.component';
import { SupersessionKey } from '../models/SupersessionKey';
import { CommercePropertyValueEnum } from '../../../modules/allowable-freight/utils/CommercePropertyValueEnum';
import { getInventoryLevelRangesAsync } from '../../../actions/DataActionExtension.g';
import { IItemInventoryLevelProfileEntity, IProfileDataMap } from '../../../actions/DataServiceEntities.g';
import getExtensionPropertyValueByKey from '../../../utils/getExtensionPropertyValueByKey';
import { EligibleItems } from '../../../modules/eve-quick-order/eve-quick-order';
import GetProductEligibilityAction, { GetProductEligibilityInput } from '../../../actions/get-product-eligibility.action';
import getProductSupersessionProperty from '../models/getProductSupersessionProperty';
import { checkAlternateWarehouseFeatureAccess, convertStringToArray } from '../../../utils/alternateWarehouseHelper';
import InventoryAvailabilityModal from '../../../utils/InventoryAvailabilityModal';
import InventoryClickIcon from '../../../utils/InventoryClickIcon';
/**
 * DescriptionInterval.
 */
enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProductCards = (
    props: PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>>,
    eligbilityList: EligibleItems,
    isAuthenticated: boolean,
    inventoryRangesList: IProfileDataMap[],
    hasInvoice: boolean,
    courseId: any,
    setAvailabilityModalProduct?: React.Dispatch<React.SetStateAction<number>>
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    const canAddToCart = (pr: ProductSearchResult): boolean => {
        if (!isAuthenticated) {
            return false;
        }

        const item: any = eligbilityList.find(i => i.ItemId === pr.ItemId);
        const currentInvString = getProductSupersessionProperty(pr, SupersessionKey.Inventory) || '0';
        const currentInventory = parseInt(currentInvString, 10);
        let invProfOos: IItemInventoryLevelProfileEntity | undefined;

        inventoryRangesList.forEach(ir => {
            const tempInvRange = ir.Profile?.find(p => p.ItemId === pr.ItemId && p.Code === 'OOS');
            if (tempInvRange) {
                invProfOos = tempInvRange;
            }
        });

        const OosUpperBound = invProfOos?.UpperBoundInclusive !== undefined ? invProfOos.UpperBoundInclusive : 0;
        const isOutOfStock = currentInventory <= OosUpperBound;

        return item?.IsEligible && !isOutOfStock && !hasInvoice && !courseId;
    };

    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) => {
                    const canAddItemToCart = canAddToCart(product);
                    return renderProductCardsDescription(
                        props,
                        product,
                        index,
                        canAddItemToCart ? setAvailabilityModalProduct : undefined,
                        false
                    );
                })}
            </ul>
        </Node>
    );
};

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>>,
    product: ProductSearchResult,
    index: number,
    setAvailabilityModalProduct?: React.Dispatch<React.SetStateAction<number>>,
    isEnabledProductDescription?: boolean
): JSX.Element | null => {
    const { config, context, resources } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    // @ts-ignore
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator,
        loginToViewPricingText: resources.loginToViewPricing
    };
    return (
        <li className={classname} key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={config.imageSettings}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={isAllowBack}
                id={props.id}
                typeName={props.typeName}
                data={{ product }}
                quickViewButton={quickviewslot}
                isEnabledProductDescription={isEnabledProductDescription}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                setAvailabilityModalProduct={setAvailabilityModalProduct}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (
    props: PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>>,
    eligbilityList: EligibleItems,
    isAuthenticated: boolean,
    inventoryRangesList: IProfileDataMap[],
    hasInvoice: boolean,
    courseId: any,
    setAvailabilityModalProduct?: React.Dispatch<React.SetStateAction<number>>
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    const canAddToCart = (pr: ProductSearchResult): boolean => {
        if (!isAuthenticated) {
            return false;
        }

        const item: any = eligbilityList.find(i => i.ItemId === pr.ItemId);
        const currentInvString = getProductSupersessionProperty(pr, SupersessionKey.Inventory) || '0';
        const currentInventory = parseInt(currentInvString, 10);
        let invProfOos: IItemInventoryLevelProfileEntity | undefined;

        inventoryRangesList.forEach(ir => {
            const tempInvRange = ir.Profile?.find(p => p.ItemId === pr.ItemId && p.Code === 'OOS');
            if (tempInvRange) {
                invProfOos = tempInvRange;
            }
        });

        const OosUpperBound = invProfOos?.UpperBoundInclusive !== undefined ? invProfOos.UpperBoundInclusive : 0;
        const isOutOfStock = currentInventory <= OosUpperBound;

        return item?.IsEligible && !isOutOfStock && !hasInvoice && !courseId;
    };
    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) => {
                    const canAddItemToCart = canAddToCart(product);
                    return index % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                        index % productDescriptionInterval === productDescriptionIntervalRemainder9
                        ? renderProductCardsDescription(
                              props,
                              product,
                              index,
                              canAddItemToCart ? setAvailabilityModalProduct : undefined,
                              true
                          )
                        : renderProductCardsDescription(
                              props,
                              product,
                              index,
                              canAddItemToCart ? setAvailabilityModalProduct : undefined,
                              false
                          );
                })}
            </ul>
        </Node>
    );
};

const renderExpandOrRefineResults = (
    props:
        | React.PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<IProductSearchResultExtensionData>>
        | undefined
) => {
    if (MsDyn365.isBrowser && typeof window !== 'undefined') {
        const url = new URL(window.location.href);

        // Check if 'find-products' exists in the URL
        if (url.href.includes('find-products')) {
            return null;
        }

        const query = url.searchParams.get('q');

        if (!query) {
            return undefined;
        }
        const trimmedQuery = query.trim();

        const hasMultipleTerms = /\s|-/g.test(trimmedQuery);

        if (!hasMultipleTerms) {
            return undefined;
        }

        let link;
        if (!url.searchParams.has('expand') && hasMultipleTerms) {
            // Render "Expand" link only if multiple terms are present
            url.searchParams.append('expand', 'true');
            link = <a href={url?.toString() || '#'}>{props?.resources.expandResultsText}</a>;
        } else {
            if (url.searchParams.has('skip')) {
                url.searchParams.delete('skip');
            }
            url.searchParams.delete('expand');
            link = <a href={url?.toString() || '#'}>{props?.resources.refineResultsText}</a>;
        }

        return (
            <span>
                {props?.resources.notFindingText} {link}
            </span>
        );
    }
    return undefined;
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitleCount = (
    titleProps: ITitleViewProps,
    enableSearchWrap: boolean,
    props:
        | React.PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<IProductSearchResultExtensionData>>
        | undefined
): JSX.Element | null => {
    const { title, TitleContainer } = titleProps;
    return (
        <Node {...TitleContainer}>
            {title.titleCount}
            {enableSearchWrap ? renderExpandOrRefineResults(props) : undefined}
        </Node>
    );
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps &
    ISearchResultContainerProps<IProductSearchResultExtensionData>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        productPrices,
        context: {
            request,
            cultureFormatter,
            actionContext,
            actionContext: {
                requestContext: {
                    gridSettings,
                    user: { isAuthenticated }
                }
            }
        },
        resources: {
            filterText,
            partNoHeading,
            descriptionHeading,
            availabilityHeading,
            quantityHeading,
            listPriceHeading,
            yourPriceHeading,
            // loginToViewPricing,
            loginToViewPricingAndAvailabilityText,
            supersededFromText,
            priceAndAvailabilityHeading,
            goToCartText,
            continueShoppingText,
            addToCartModalHeading,
            addToCartText
        },
        data: { cart, listPageState: productSearchResults, manufacturerCodes },
        config: { enableProdutDescription, listView, imageSettings }
    } = props;

    const [activeProducts, setActiveProducts] = React.useState<ProductSearchResult[]>([]);

    // Fix for issue 8251: Boost relevance of Part Number matches
    // This change prioritizes matched products by ItemId and manufacturer codes
    // to improve search relevance and address the issue of items flipping

    React.useEffect(() => {
        if (request.query) {
            const searchTerm = request.query['q'];
            const codes = manufacturerCodes?.result as any;

            const matchedProducts: ProductSearchResult[] = [];
            const unmatchedProducts: ProductSearchResult[] = [];

            productSearchResults.result?.activeProducts.forEach(p => {
                let matched = false;

                // Check for direct match by ItemId
                if (
                    searchTerm &&
                    (p.ItemId?.toLowerCase() === searchTerm.toLowerCase() ||
                        p.ItemId?.substring(3, p.ItemId.length).toLowerCase() === searchTerm.toLowerCase())
                ) {
                    matched = true;
                } else {
                    // Check for manufacturer code match
                    for (let i = 0; i < codes.manufacturerCodes.length; i++) {
                        if (codes.manufacturerCodes[i].GDNManufacturerCode + searchTerm === p.ItemId) {
                            matched = true;
                            break;
                        }
                    }
                }

                if (matched) {
                    matchedProducts.push(p);
                } else {
                    unmatchedProducts.push(p);
                }
            });
            setActiveProducts([...matchedProducts, ...unmatchedProducts]);
        }
    }, [request.query, manufacturerCodes, productSearchResults.result]);

    const [eligbilityList, setEligbilityList] = useState<EligibleItems>([]);
    const [isEligibilityChecked, setEligibilityChecked] = useState<boolean>(false);
    const [areInventoryRangesChecked, setAreInventoryRangesChecked] = useState<boolean>(false);
    const [inventoryRangesList, setInventoryRangesList] = useState<IProfileDataMap[]>([]);

    const getProductEligibility = async (): Promise<void> => {
        if (isEligibilityChecked) {
            return;
        }
        const itemIds = props.data.products?.result?.products.map(p => p.ItemId!) || [];
        if (!itemIds.length) {
            return;
        }
        const cart = props.data.cart.result?.cart;
        // if (!cart) {
        //   return;
        // }
        const input = new GetProductEligibilityInput(itemIds, false, cart?.Id || '', null, null, null);
        const eligibilityData = await GetProductEligibilityAction(input, props.context.actionContext);
        setEligibilityChecked(true);
        let list: EligibleItems = [];

        eligibilityData.forEach((i: any) => {
            const id = getExtensionPropertyValueByKey(i.ExtensionProperties, 'ItemId', CommercePropertyValueEnum.string);
            list.push({
                ItemId: String(id),
                IsEligible: i.IsEligible,
                IsAssorted: true
            });
        });
        setEligbilityList(list);
        void getInvProfs();
    };

    const getInvProfs = async (): Promise<void> => {
        if (areInventoryRangesChecked) {
            return;
        }
        const itemIds = props.data.products?.result?.products.map(p => p.ItemId!) || [];
        const resp = await getInventoryLevelRangesAsync({ callerContext: props.context.actionContext }, itemIds);
        setAreInventoryRangesChecked(true);
        setInventoryRangesList(resp);
    };

    // Determines whether the alternate warehouse feature should be displayed for the user.
    const [shouldDisplayAlternateWarehouseFeature, setShouldDisplayAlternateWarehouseFeature] = React.useState(false);

    // List of customer account numbers that are authorized to use the alternate warehouse feature.
    const authorizedWarehouseAccounts: string[] = props.context.app.config.authorizedWarehouseAccounts || [];

    // Configuration flag to enable or disable the alternate warehouse feature.
    const isAlternateWarehouseFeatureEnabled: boolean = props.context.app.config.enableAlternateWarehouseFeature;

    // List of dealer types that are authorized to use the alternate warehouse feature.
    const allowedDealerTypes: string = props.context.app.config.allowedDealerTypes || '';

    // List of comma- or semicolon-separated string (e.g., '01,16,99') customer groups that are authorized to use the alternate warehouse feature.
    const designatedCustomerGroups: string = props.context.app.config.designatedCustomerGroups || '';

    // convert customer groups into an array of strings
    const designatedCustomerGroupsArr = convertStringToArray(designatedCustomerGroups);
    const allowedDealerTypesArr = convertStringToArray(allowedDealerTypes);

    React.useEffect(() => {
        const checkAlternateWarehouseFeature = async () => {
            const shouldDisplayAlternateWarehouse = await checkAlternateWarehouseFeatureAccess({
                extensionProperties: props.data.products.result?.products[0].ExtensionProperties,
                allowedDealerTypes: allowedDealerTypesArr,
                designatedCustomerGroupsArr,
                authorizedWarehouseAccounts,
                customerAccNum: props.context.request.user.customerAccountNumber,
                isAlternateWarehouseFeatureEnabled
            });

            setShouldDisplayAlternateWarehouseFeature(shouldDisplayAlternateWarehouse);
        };

        checkAlternateWarehouseFeature();
    }, []);

    React.useEffect(() => {
        if (isAuthenticated) {
            void getProductEligibility();
        }
    }, [props.data.products]);

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [addedProduct, setAddedProduct] = React.useState<SimpleProduct | undefined>(undefined);
    const [productPricing, setProductPricing] = React.useState<ProductPrice | undefined>(undefined);
    const [availabilityModalProduct, setAvailabilityModalProduct] = React.useState<number>(0);
    const toggle = () => {
        setIsExpanded(!isExpanded);
    };

    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    const setModalProps = (product: SimpleProduct, price: ProductPrice): IPopupProps => {
        return {
            modalOpen: modalOpen,
            context: props.context,
            className: 'list-view__modal',
            gridSettings,
            imageSettings,
            priceComponent: props.component,
            id: '1',
            productQuantity: 1,
            navigationUrl: getUrlSync('cart', actionContext),
            setModalOpen: value => setModalOpen(value),
            typeName: 'Add To Cart',
            data: { product, price },
            dialogStrings: {
                goToCartText: goToCartText,
                currentPriceText: '',
                continueShoppingText: continueShoppingText,
                freePriceText: '',
                headerItemOneText: addToCartModalHeading,
                headerItemFormatText: '',
                originalPriceText: '',
                headerMessageText: ''
            }
        };
    };

    const handleAddToCart = (productId: number, quantity: number) => {
        const foundProduct = activeProducts.find(pr => pr.RecordId === productId);
        if (!foundProduct) {
            return;
        }
        const product: SimpleProduct = {
            BasePrice: foundProduct.BasePrice || foundProduct.Price,
            AdjustedPrice: foundProduct.Price,
            Name: foundProduct.Name,
            RecordId: foundProduct.RecordId,
            ItemId: foundProduct.ItemId,
            ProductTypeValue: 1,
            Price: foundProduct.Price
        };
        setAddedProduct(product);
        setProductPricing(getProductPrice(productPrices, product.RecordId));
        cart.result
            ?.addProductToCart({
                product,
                count: quantity
            })
            .then(response => {
                if (response.status === 'SUCCESS' && props.app.config.addToCartBehavior === 'showModal') {
                    setModalOpen(true);
                }
            });
    };

    const getProductSupersessionProperty = (product: ProductSearchResult, extensionKey: SupersessionKey) => {
        const properties = product.ExtensionProperties;
        if (properties) {
            return properties.find(pr => pr.Key === extensionKey)?.Value?.StringValue || null;
        }
        return null;
    };

    const renderAddtoCartButton = (pr: ProductSearchResult, courseId: any, courseInCartText: string) => {
        if (isAuthenticated) {
            const item: any = eligbilityList.find(i => i.ItemId === pr.ItemId);

            const currentInvString = getProductSupersessionProperty(pr, SupersessionKey.Inventory) || '0';
            const currentInventory = parseInt(currentInvString, 10);
            let invProfOos: IItemInventoryLevelProfileEntity | undefined;
            inventoryRangesList.map(ir => {
                const tempInvRange = ir.Profile?.find(p => p.ItemId === pr.ItemId && p.Code === 'OOS');
                if (tempInvRange) {
                    invProfOos = tempInvRange;
                }
            });

            const OosUpperBound =
                invProfOos?.hasOwnProperty('UpperBoundInclusive') && invProfOos?.UpperBoundInclusive !== undefined
                    ? invProfOos.UpperBoundInclusive
                    : 0;
            const isOutOfStock = currentInventory <= OosUpperBound;

            if (item?.IsEligible && !isOutOfStock) {
                if (props.data.cart.result?.hasInvoiceLine) {
                    return (
                        <td>
                            <div className='eduerrors'>{props.resources.invoiceInCartText}</div>
                        </td>
                    );
                }
                if (courseId) {
                    return (
                        <td>
                            <div className='eduerrors'>{courseInCartText}</div>
                        </td>
                    );
                }
                return <AddToCartListComponent productId={pr.RecordId} onAddToCart={handleAddToCart} addToCartButtonText={addToCartText} />;
            } else {
                return (
                    <td colSpan={2} style={{ textAlign: 'right' }}>
                        {item?.ItemId ? props.resources.productEligibilityText : ' '}
                    </td>
                );
            }
        }
        return null;
    };

    const renderAvailabilityModal = () => {
        const allProducts = props.data.products.result?.products;
        const modalProduct = allProducts?.find(p => p.RecordId === availabilityModalProduct);
        return (
            <InventoryAvailabilityModal
                modalProduct={modalProduct}
                toggle={() => setAvailabilityModalProduct(0)}
                isOpen={availabilityModalProduct !== 0}
                config={props.context.app.config}
            />
        );
    };

    // const _renderInventoryClickIcon = (id: number, isAddToCartButton: boolean) => {
    //     const [tooltipOpen, setTooltipOpen] = React.useState(false);

    //     // const warehouseJsonData = getExtensionPropertyValueByKey(
    //     //     extensionProperties,
    //     //     SupersessionKey.InventoryBreakdown,
    //     //     CommercePropertyValueEnum.string
    //     // ) as string;

    //     // const data = JSON.parse(warehouseJsonData) as WarehouseInventoryType[];
    //     // const isPrimaryWarehouseInStock = data?.[0]?.Available > 0;

    //     if (!shouldDisplayAlternateWarehouseFeature || !isAddToCartButton) {
    //         return null;
    //     }

    //     const toggleTooltip = () => {
    //         setTooltipOpen(!tooltipOpen);
    //     };
    //     return (
    //         <div>
    //             <span
    //                 id={'Tooltip-' + id}
    //                 onClick={() => {
    //                     setTooltipOpen(false);
    //                     setAvailabilityModalProduct(id);
    //                 }}
    //                 data-toggle='tooltip'
    //             >
    //                 <svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 30 30'>
    //                     <path d='M15,3C8.373,3,3,8.373,3,15c0,6.627,5.373,12,12,12s12-5.373,12-12C27,8.373,21.627,3,15,3z M16,21h-2v-7h2V21z M15,11.5 c-0.828,0-1.5-0.672-1.5-1.5s0.672-1.5,1.5-1.5s1.5,0.672,1.5,1.5S15.828,11.5,15,11.5z'></path>
    //                 </svg>
    //             </span>
    //             <Tooltip isOpen={tooltipOpen} target={'Tooltip-' + id} toggle={toggleTooltip} placement='right' className='custom-tooltip'>
    //                 Check availability
    //             </Tooltip>
    //         </div>
    //     );
    // };

    let courseId: string | number | boolean | null | undefined;
    let cartObj = props.data.cart.result?.cart;
    let cartlines = cartObj?.CartLines;
    if (cartlines && cartlines.length) {
        courseId = getExtensionPropertyValueByKey(cartlines[0].ExtensionProperties, 'CourseId', CommercePropertyValueEnum.string);
    }
    const _renderProductsListView = () => {
        const { courseInCartText } = props.resources;
        return (
            <Table className={'list-view__table'}>
                <thead>
                    <tr>
                        <th />
                        <th>{partNoHeading}</th>
                        <th>{descriptionHeading}</th>
                        {isAuthenticated ? <th>{availabilityHeading}</th> : null}
                        <th>{listPriceHeading}</th>
                        <th>{isAuthenticated ? yourPriceHeading : priceAndAvailabilityHeading}</th>
                        {isAuthenticated ? (
                            <>
                                <th>{quantityHeading}</th>
                                <th />
                            </>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {productSearchResults.result?.activeProducts.map(pr => {
                        // const pricing = getProductPrice(productPrices, pr.RecordId);
                        // const inventoryLabel = getInventoryLabel(configurationObject?.result?.channelInventoryConfigurationId, pr);
                        const priceComponent = <span>{cultureFormatter.formatCurrency(pr.BasePrice || pr.Price)}</span>;
                        const addToCartButton = renderAddtoCartButton(pr, courseId, courseInCartText);
                        const isAddToCartButton = React.isValidElement(addToCartButton) && addToCartButton.type === AddToCartListComponent;
                        return (
                            <tr key={pr.RecordId}>
                                <td>
                                    {renderProductPlacementImage(
                                        imageSettings,
                                        gridSettings,
                                        `/Products/${pr.ItemId}_000_001.png`,
                                        pr.PrimaryImageUrl,
                                        pr.Name,
                                        request
                                    )}
                                </td>
                                <td>
                                    <a href={getProductPageUrlSync(pr.Name!, pr.RecordId, actionContext)}>{pr.ItemId}</a>
                                    <br />
                                    {getProductSupersessionProperty(pr, SupersessionKey.SupersededId) ? (
                                        <p className={'supersession-text'}>
                                            {supersededFromText} {getProductSupersessionProperty(pr, SupersessionKey.SupersededId)}
                                        </p>
                                    ) : null}
                                </td>
                                <td>
                                    {pr.Name}
                                    <br />
                                    {getProductSupersessionProperty(pr, SupersessionKey.SupersededName) ? (
                                        <p className={'supersession-text'}>
                                            {supersededFromText} {getProductSupersessionProperty(pr, SupersessionKey.SupersededName)}
                                        </p>
                                    ) : null}
                                </td>
                                {isAuthenticated ? (
                                    <td className={'centered-cell'}>
                                        <div className='availability-modal'>
                                            <p
                                                className={
                                                    shouldDisplayAlternateWarehouseFeature && isAddToCartButton
                                                        ? ''
                                                        : 'availability-modal__inventory'
                                                }
                                            >
                                                {getProductSupersessionProperty(pr, SupersessionKey.Inventory)}
                                            </p>
                                            <InventoryClickIcon
                                                id={pr.RecordId}
                                                onClick={setAvailabilityModalProduct}
                                                shouldDisplay={shouldDisplayAlternateWarehouseFeature && isAddToCartButton}
                                            />
                                        </div>
                                    </td>
                                ) : null}
                                <td>{priceComponent}</td>
                                {/* {LoginToViewPricingComponent(props.context.request, loginToViewPricing, XXX
                    priceComponent, props.config.onlyShowPriceWhenAuthenticated ?? true)}</td> */}

                                <td>
                                    {LoginToViewPricingComponent(
                                        props.context.request,
                                        loginToViewPricingAndAvailabilityText,
                                        <>{cultureFormatter.formatCurrency(pr.Price)}</>,
                                        isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated
                                    )}
                                    {/* {(isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated) ? cultureFormatter.formatCurrency(pr.Price) : null} */}
                                </td>
                                {addToCartButton}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const showFilterDropdown = props.config.showFilterDropdown;
    const showSortDropdown = props.config.showSortDropdown;

    const enableSearchWrap = props.context.app.config.enableSearchWrap;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                        {renderTitleCount(TitleViewProps, enableSearchWrap, props)}
                    </Node>
                )}
                {choiceSummary}
                <Node className='msc-sort-filter-wrapper'>{showFilterDropdown && showSortDropdown && modalToggle}</Node>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}
                {enableProdutDescription
                    ? renderProducts(
                          props,
                          eligbilityList,
                          isAuthenticated,
                          inventoryRangesList,
                          props.data.cart.result?.hasInvoiceLine || false,
                          courseId,
                          setAvailabilityModalProduct
                      )
                    : renderProductCards(
                          props,
                          eligbilityList,
                          isAuthenticated,
                          inventoryRangesList,
                          props.data.cart.result?.hasInvoiceLine || false,
                          courseId,
                          setAvailabilityModalProduct
                      )}
                {pagination}
                {renderAvailabilityModal()}
            </Module>
        );
    }

    return (
        <Module {...SearchResultContainer}>
            {/* @ts-ignore */}
            {modalOpen && addedProduct ? <Popup {...setModalProps(addedProduct, productPricing)} /> : null}
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {TitleViewProps && renderTitleCount(TitleViewProps, enableSearchWrap, props)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                {(showFilterDropdown || showSortDropdown) && (
                    <Node className='ms-refiner-heading'>
                        <Node className='ms-refiner-heading_title'>
                            {showFilterDropdown && (
                                <Button
                                    className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                                    aria-label='Filter'
                                    onClick={toggle}
                                    aria-expanded={isExpanded}
                                    tabIndex={0}
                                >
                                    {filterText}
                                </Button>
                            )}
                            <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                            {showSortDropdown && (
                                <div className='ms-search-result-wrapper-sort-by-category'>
                                    {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                                </div>
                            )}
                        </Node>
                        {refineMenu && isExpanded && renderRefiner(refineMenu)}
                    </Node>
                )}
                <Node {...ProductSectionContainer}>
                    {errorMessage}
                    {/* {enableProdutDescription ? renderProducts(props) : renderProductCards(props)} */}
                    {listView
                        ? _renderProductsListView()
                        : enableProdutDescription
                        ? renderProducts(
                              props,
                              eligbilityList,
                              isAuthenticated,
                              inventoryRangesList,
                              props.data.cart.result?.hasInvoiceLine || false,
                              courseId,
                              setAvailabilityModalProduct
                          )
                        : renderProductCards(
                              props,
                              eligbilityList,
                              isAuthenticated,
                              inventoryRangesList,
                              props.data.cart.result?.hasInvoiceLine || false,
                              courseId,
                              setAvailabilityModalProduct
                          )}
                    {pagination}
                </Node>
            </Node>
            {renderAvailabilityModal()}
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h1' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderProductPlacementImage = (
    productCardimageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    requestContext?: IRequestContext
): JSX.Element | null => {
    if (!imageUrl || !gridSettings || !productCardimageSettings) {
        return null;
    }
    if (productCardimageSettings) {
        productCardimageSettings.viewports = {
            lg: { h: 100, w: 100, q: '80' },
            md: { h: 100, w: 100, q: '80' },
            sm: { h: 100, w: 100, q: '80' },
            xs: { h: 100, w: 100, q: '80' }
        };
    }
    const image: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageUrl
    };
    const imageProps: IImageProps = { gridSettings };

    imageProps.gridSettings = gridSettings;
    imageProps.imageSettings = productCardimageSettings;
    imageProps.imageSettings.cropFocalRegion = true;
    return <Image {...image} {...imageProps} loadFailureBehavior='empty' requestContext={requestContext} className={'eve-listview__img'} />;
};

const getProductPrice = (pricing: ProductPrice[] | undefined, productRecId: number): ProductPrice | undefined => {
    if (!pricing) {
        return undefined;
    }
    return pricing.find(pr => pr.ProductId === productRecId);
};

export default SearchResultContainerView;
